// application/useCases/retireFundingSource.js
import { createVersionTimeline, currentAsOf } from '../../domain/model/VersionTimeline';
import { commitFundingSourceVersion } from './commitFundingSourceVersion';

/**
 * Retires a payer, as a version effective today.
 *
 * A VERSION RATHER THAN A FLAG, because `isActive` is part of the record and the whole record
 * versions together. Retiring is therefore an ordinary append — same transaction, same numbering,
 * same collision handling — and it lands as "from today, this payer is inactive" rather than
 * rewriting what the record has always said. The audit falls out of that: the version's `updateBy` is
 * whoever retired it, and because a version in force is never rewritten, that value cannot be
 * overwritten by a later edit. The explicit `inactivatedBy` stamp existed only to survive exactly
 * that, and is no longer needed.
 *
 * IT MUST CANCEL A SCHEDULED CHANGE, and this is the part that is not obvious. A version scheduled
 * for next month carries the record as it was then drafted — `isActive: true` — so on its effective
 * date it would become the version in force and quietly bring the payer back. "Inactivation cannot be
 * undone" would then be broken by a pending change nobody revisited. `commitFundingSourceVersion`
 * already cancels scheduled versions on other dates, so retiring inherits the behavior it needs;
 * what it does NOT inherit is the warning, so a caller confirming a retirement has to say that a
 * scheduled change is being dropped.
 *
 * REFUSED WHEN ALREADY INACTIVE, rather than written idempotently. A second retirement would append a
 * version that changes nothing, and a timeline is a record of decisions — an entry nobody decided is
 * noise in the history the collection exists to keep.
 *
 * @param {{ fundingSourceId: string, clinicId: string, asOf: string }} scope - `asOf` is
 *   'YYYY-MM-DD' and comes from the edge; it is both the retirement's effective date and what
 *   "currently in force" is judged against.
 * @param {import('../ports/FundingSourceRepository.port').FundingSourceRepository} repository
 * @returns {Promise<string>} the new version's id
 * @throws {RangeError} the payer has no version in force, or is already retired
 */
// eslint-disable-next-line import/prefer-default-export
export async function retireFundingSource({ fundingSourceId, clinicId, asOf }, repository) {
  const versions = await repository.loadVersions({ fundingSourceId, clinicId });
  const inForce = currentAsOf(createVersionTimeline(versions), asOf);

  if (inForce === null) {
    throw new RangeError(`Funding source ${fundingSourceId} has no version in force on ${asOf}.`);
  }

  // The timeline carries coordinates only, so the resolved head is matched back to the record it
  // describes — the record this retirement has to carry forward unchanged apart from its status.
  const record = versions.find((version) => version.versionId === inForce.versionId);

  if (record.isActive === false) {
    throw new RangeError(`Funding source ${fundingSourceId} is already retired.`);
  }

  return commitFundingSourceVersion({
    fundingSourceId,
    clinicId,
    asOf,
    effectiveFrom: asOf,
    record: { ...record, isActive: false },
  }, repository);
} // retireFundingSource()
